import React, { useState, useEffect, useMemo, useRef } from "react";
import { Button, Form, Spinner, Badge, Modal, InputGroup } from "react-bootstrap";
import { 
  Plus, Trash2, CheckCircle2, Search, Star, ListTodo, X, Check, ChevronLeft
} from "lucide-react";
import { useChecklists } from "../hooks/useChecklists";

const ChecklistenManager = ({ auth }) => {
  const {
    checklists, loading, error, expandedId, setExpandedId,
    newChecklistTitle, setNewChecklistTitle, newItemText, setNewItemText,
    handleCreateChecklist, handleDeleteChecklist, handleRenameChecklist,
    handleAddItem, handleToggleItem, handleRemoveItem, loadItems,
    handleUpdateItemDate, handleUpdateItemDescription,
    handleArchiveChecklist,
  } = useChecklists({
    workspaceId: localStorage.getItem("workspaceId"),
    token: auth.user?.id_token,
  });

  // UI State
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("active");
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedback, setFeedback] = useState({ rating: 5, comment: "" });
  const [isCreating, setIsCreating] = useState(false);
  const creationInputRef = useRef(null);

  // Mobile Navigation Logic
  const showSidebar = !expandedId; 

  useEffect(() => {
    const lastId = localStorage.getItem("lastOpenedChecklist");
    if (window.innerWidth > 768 && lastId && !expandedId && checklists.length > 0) {
      const id = parseInt(lastId);
      if (checklists.find(c => c.id === id)) {
        setExpandedId(id);
        loadItems(id);
      }
    }
  }, [checklists, expandedId, setExpandedId, loadItems]);

  const handleArchiveSubmit = async () => {
    if (!expandedId) return;
    try {
      await handleArchiveChecklist(expandedId, feedback.rating, feedback.comment);
      setShowFeedbackModal(false);
      setExpandedId(null);
      setFeedback({ rating: 5, comment: "" });
      localStorage.removeItem("lastOpenedChecklist");
    } catch (err) {
      console.error("Archive failed", err);
    }
  };

  const handleSelectChecklist = (id) => {
    setExpandedId(id);
    loadItems(id);
    localStorage.setItem("lastOpenedChecklist", id);
  };

  const handleRecreate = async (oldChecklist) => {
    try {
      const newChecklist = await handleCreateChecklist(`${oldChecklist.title} (Neu)`);
      if (newChecklist && oldChecklist.items) {
        for (const item of oldChecklist.items) {
          await handleAddItem(newChecklist.id, item.description);
        }
        setFilter("active");
        handleSelectChecklist(newChecklist.id);
      }
    } catch (err) {
      console.error("Recreation failed:", err);
    }
  };

  const submitNewChecklist = async () => {
    if (!newChecklistTitle.trim()) {
      setIsCreating(false);
      return;
    }
    await handleCreateChecklist(); 
    setIsCreating(false);
    setNewChecklistTitle("");
  };

  const filteredChecklists = useMemo(() => {
    return checklists
      .filter(c => {
        const matchesSearch = c.title.toLowerCase().includes(searchQuery.toLowerCase());
        return filter === "completed" 
          ? (matchesSearch && c.status === "archived")
          : (matchesSearch && c.status === "active");
      })
      .sort((a, b) => {
        const dateA = filter === "completed" ? new Date(a.archivedAt) : new Date(a.createdAt);
        const dateB = filter === "completed" ? new Date(b.archivedAt) : new Date(b.createdAt);
        return dateB - dateA;
      });
  }, [checklists, searchQuery, filter]);

  const activeChecklist = checklists.find(c => c.id === expandedId);

  if (loading && checklists.length === 0) return (
    <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
      <Spinner animation="border" variant="primary" />
    </div>
  );

  return (
    <div className="d-flex bg-white flex-grow-1">
      
      {/* --- SIDEBAR --- */}
      <div 
        className={`${showSidebar ? 'd-flex' : 'd-none'} d-md-flex flex-column border-end bg-light`} 
        style={{ width: window.innerWidth > 768 ? "350px" : "100%", minWidth: window.innerWidth > 768 ? "350px" : "100%" }}
      >
        <div className="p-3 border-bottom bg-white">
          <h5 className="fw-bold d-flex align-items-center gap-2 mb-3">
            <ListTodo size={20} className="text-primary" /> Checklisten
          </h5>
          <div className="position-relative mb-2">
            <Search size={16} className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" />
            <Form.Control 
              placeholder="Suchen..." 
              className="ps-5 border-0 bg-light shadow-none"
              style={{ fontSize: "0.85rem", borderRadius: "8px" }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="d-flex gap-2">
            <Button 
              variant={filter === "active" ? "primary" : "outline-secondary"} 
              size="sm" className="flex-grow-1 border-0 shadow-none" 
              onClick={() => setFilter("active")}
            >Aktiv</Button>
            <Button 
              variant={filter === "completed" ? "primary" : "outline-secondary"} 
              size="sm" className="flex-grow-1 border-0 shadow-none" 
              onClick={() => setFilter("completed")}
            >Erledigt</Button>
          </div>
        </div>

        <div className="flex-grow-1 overflow-auto p-2">
          {filteredChecklists.map(c => (
            <div 
              key={c.id}
              onClick={() => handleSelectChecklist(c.id)}
              className={`p-3 mb-2 rounded-3 cursor-pointer transition-all ${expandedId === c.id ? 'bg-white shadow-sm border-start border-4 border-primary' : 'hover-bg-gray'}`}
            >
              <div className="d-flex justify-content-between align-items-start">
                <div className="fw-bold text-truncate" style={{ fontSize: "0.9rem", maxWidth: '75%' }}>{c.title}</div>
                {c.status === 'archived' && (
                  <div className="text-warning d-flex align-items-center" style={{ fontSize: "0.75rem" }}>
                    <Star size={12} className="fill-warning me-1" /> {c.rating}
                  </div>
                )}
              </div>
              <div className="d-flex justify-content-between align-items-center mt-1">
                <span className="text-muted" style={{ fontSize: "0.7rem" }}>
                  {c.status === 'archived' 
                    ? `Archiviert: ${new Date(c.archivedAt).toLocaleDateString()}` 
                    : `Erstellt: ${new Date(c.createdAt).toLocaleDateString()}`}
                </span>
                <Badge bg="light" text="dark" className="border" style={{ fontSize: "0.6rem" }}>
                  {c.items?.length || 0} Items
                </Badge>
              </div>
            </div>
          ))}

          {isCreating && filter === "active" && (
            <div className="p-2 bg-white rounded-3 shadow-sm border mb-2">
              <Form.Control
                ref={creationInputRef}
                autoFocus size="sm" placeholder="Name der Liste..."
                className="border-0 mb-2 shadow-none"
                value={newChecklistTitle}
                onChange={(e) => setNewChecklistTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitNewChecklist()}
              />
              <div className="d-flex justify-content-end gap-1">
                <Button variant="light" size="sm" onClick={() => setIsCreating(false)}><X size={14} /></Button>
                <Button variant="primary" size="sm" onClick={submitNewChecklist}><Check size={14} /></Button>
              </div>
            </div>
          )}
          
          {filter === "active" && !isCreating && (
            <Button 
              variant="link" 
              className="w-100 text-primary text-decoration-none mt-2 d-flex align-items-center justify-content-center gap-2 py-2 rounded-3 hover-bg-gray"
              onClick={() => setIsCreating(true)}
            >
              <Plus size={18} /> Neue Liste erstellen
            </Button>
          )}
        </div>
      </div>

      {/* --- MAIN STAGE --- */}
      <div className={`${expandedId ? 'd-flex' : 'd-none'} d-md-flex flex-grow-1 bg-light flex-column`}>
        {activeChecklist ? (
          <>
            {/* Header */}
            <div className="px-3 px-md-4 py-3 border-bottom d-flex align-items-center justify-content-between bg-white shadow-sm" style={{ zIndex: 10 }}>
              <div className="d-flex align-items-center gap-3 overflow-hidden">
                <Button variant="light" size="sm" className="d-md-none rounded-circle p-1" onClick={() => setExpandedId(null)}>
                  <ChevronLeft size={20} />
                </Button>
                <div className="text-truncate">
                  {activeChecklist.status === 'archived' ? (
                    <h4 className="fw-bold mb-0 text-truncate">{activeChecklist.title}</h4>
                  ) : (
                    <Form.Control
                      className="fw-bold fs-4 border-0 p-0 shadow-none bg-transparent"
                      value={activeChecklist.title}
                      onChange={(e) => handleRenameChecklist(activeChecklist.id, e.target.value)}
                    />
                  )}
                  <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>
                    {activeChecklist.status === 'archived' 
                      ? `Beendet am ${new Date(activeChecklist.archivedAt).toLocaleDateString()}` 
                      : `Erstellt am ${new Date(activeChecklist.createdAt).toLocaleDateString()}`}
                  </small>
                </div>
              </div>

              <div className="d-flex gap-2">
                {activeChecklist.status === 'active' ? (
                  <>
                    <Button variant="outline-success" size="sm" className="rounded-pill px-2 px-md-3 d-flex align-items-center gap-2" onClick={() => setShowFeedbackModal(true)}>
                      <CheckCircle2 size={16} /> <span className="d-none d-sm-inline">Abschließen</span>
                    </Button>
                    <Button variant="outline-danger" size="sm" className="rounded-circle p-2" onClick={() => handleDeleteChecklist(activeChecklist.id)}>
                      <Trash2 size={16} />
                    </Button>
                  </>
                ) : (
                  <Button variant="primary" size="sm" className="rounded-pill px-3" onClick={() => handleRecreate(activeChecklist)}>
                    <Plus size={16} className="me-2" /> <span className="d-none d-sm-inline">Vorlage nutzen</span>
                  </Button>
                )}
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-grow-1 overflow-auto px-3 px-md-4 py-4">
              <div className="mx-auto w-100" style={{ maxWidth: "900px" }}>
                
                {activeChecklist.status === 'archived' ? (
                  <div className="d-flex flex-column gap-4">
                    <div className="bg-white rounded-4 border p-4 shadow-sm">
                      <h6 className="fw-bold text-muted small text-uppercase mb-3">Deine Bewertung</h6>
                      <div className="d-flex gap-1 text-warning mb-3">
                        {[1,2,3,4,5].map((star) => (
                          <Star key={star} size={24} className={star <= activeChecklist.rating ? "fill-warning" : "text-light"} />
                        ))}
                      </div>
                      {activeChecklist.feedbackComment && (
                        <div className="mt-3">
                          <h6 className="fw-bold text-muted small text-uppercase">Feedback / Notizen</h6>
                          <p className="bg-light p-3 rounded-3 border-start border-4 border-primary fst-italic mb-0">
                            "{activeChecklist.feedbackComment}"
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <h6 className="text-uppercase text-muted fw-bold mb-3 small">Items dieser Liste</h6>
                      <div className="bg-white rounded-4 border p-2 p-md-3 shadow-sm">
                        {activeChecklist.items?.map(item => (
                          <div key={item.id} className="d-flex align-items-center gap-3 p-3 border-bottom last-border-0">
                            <Check size={18} className={item.isDone ? "text-success" : "text-muted"} />
                            <span className={item.isDone ? "text-decoration-line-through text-muted" : ""}>
                              {item.description}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <h6 className="text-uppercase text-muted fw-bold mb-3 small tracking-wider">To-Do Items</h6>
                    <div className="d-flex flex-column gap-2 mb-4">
                      {activeChecklist.items?.map(item => (
                        <div key={item.id} className="d-flex align-items-center gap-3 p-3 rounded-4 border bg-white shadow-sm item-hover-effect transition-all">
                          <Form.Check 
                            type="checkbox" checked={item.isDone}
                            onChange={() => handleToggleItem(activeChecklist.id, item.id, item.isDone)}
                          />
                          <div className="flex-grow-1">
                            <Form.Control 
                              className={`border-0 p-0 shadow-none bg-transparent ${item.isDone ? 'text-decoration-line-through text-muted' : ''}`}
                              defaultValue={item.description}
                              onBlur={(e) => handleUpdateItemDescription(activeChecklist.id, item.id, e.target.value)}
                            />
                            <input 
                              type="date" className="border-0 text-muted p-0 mt-1 bg-transparent small w-100"
                              value={item.itemDate ? item.itemDate.slice(0, 10) : ""}
                              onChange={(e) => handleUpdateItemDate(activeChecklist.id, item.id, e.target.value)}
                            />
                          </div>
                          <Button variant="link" className="text-danger opacity-25 p-1" onClick={() => handleRemoveItem(item.id)}>
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>

                    <div className="p-2 p-md-3 rounded-4 bg-white border border-dashed text-center shadow-sm">
                      <InputGroup>
                        <Form.Control 
                          placeholder="Neues To-Do..." className="border-0 shadow-none px-3"
                          value={newItemText} onChange={(e) => setNewItemText(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleAddItem(activeChecklist.id)}
                        />
                        <Button variant="primary" className="rounded-3 px-3 px-md-4" onClick={() => handleAddItem(activeChecklist.id)}>
                          <Plus size={20} />
                        </Button>
                      </InputGroup>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted bg-light text-center px-4">
            <div className="p-5 bg-white rounded-circle shadow-sm mb-4">
                <ListTodo size={64} className="text-primary opacity-50" />
            </div>
            <h5 className="fw-bold text-dark">Bereit für den nächsten Trip?</h5>
            <p style={{ maxWidth: "300px" }}>Wähle eine Checkliste aus der Seitenleiste oder erstelle eine neue Vorlage.</p>
          </div>
        )}
      </div>

      {/* --- FEEDBACK MODAL --- */}
      <Modal show={showFeedbackModal} onHide={() => setShowFeedbackModal(false)} centered>
          <Modal.Header closeButton className="border-0">
            <Modal.Title className="fw-bold">Checkliste abschließen</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center pb-4">
            <p className="text-muted">Wie gut hat diese Liste für dein Event funktioniert?</p>
            <div className="d-flex gap-2 mb-4 justify-content-center">
              {[1,2,3,4,5].map(star => (
                <Star 
                  key={star} size={36} 
                  className={`cursor-pointer transition-all ${feedback.rating >= star ? 'text-warning fill-warning' : 'text-muted'}`}
                  onClick={() => setFeedback({...feedback, rating: star})}
                />
              ))}
            </div>
            <Form.Group className="text-start">
              <Form.Label className="small fw-bold px-1">Was können wir beim nächsten Mal besser machen?</Form.Label>
              <Form.Control 
                as="textarea" rows={3} className="rounded-3 border-light bg-light shadow-none"
                placeholder="z.B. Regenjacke fehlte auf der Liste..." 
                value={feedback.comment}
                onChange={(e) => setFeedback({...feedback, comment: e.target.value})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="light" className="w-100 rounded-3 mb-2" onClick={() => setShowFeedbackModal(false)}>Abbrechen</Button>
            <Button variant="primary" className="w-100 rounded-3 shadow-sm" onClick={handleArchiveSubmit}>
              Abschließen & Archivieren
            </Button>
          </Modal.Footer>
      </Modal>

      <style>{`
        .hover-bg-gray:hover { background-color: #f1f5f9; }
        .border-dashed { border: 2px dashed #cbd5e1 !important; }
        .item-hover-effect:hover { border-color: #3b82f6 !important; transform: translateY(-1px); }
        .fill-warning { fill: #ffc107; }
        .last-border-0:last-child { border-bottom: 0 !important; }
        
        @media screen and (max-width: 768px) {
          input, textarea { font-size: 16px !important; }
        }
      `}</style>
    </div>
  );
};

export default ChecklistenManager;